import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  downloadChannelSyncReliabilityReportCsv,
  fetchChannelSyncReliabilityReport,
  type ChannelSyncReliabilityReportParams,
  type ChannelSyncReliabilityReportResponse,
} from '../api/reports';
import {
  channelSyncTrendHasActivity,
  collapseEmptyHistory,
} from '../components/reports/collapseEmptyHistory';
import { ReportGeneratedHeader } from '../components/reports/ReportGeneratedHeader';
import { ShowEmptyDaysToggle } from '../components/reports/ShowEmptyDaysToggle';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatInstant } from '../utils/datetime';

export const ReportsSyncReliabilityPage: React.FC = () => {
  const [report, setReport] = useState<ChannelSyncReliabilityReportResponse | null>(null);
  const [days, setDays] = useState('14');
  const [channel, setChannel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmptyDays, setShowEmptyDays] = useState(false);

  const buildParams = useCallback((): ChannelSyncReliabilityReportParams => {
    const parsedDays = Number.parseInt(days, 10);
    return {
      days: Number.isNaN(parsedDays) || parsedDays < 14 ? 14 : parsedDays,
      channel: channel.trim() || undefined,
    };
  }, [days, channel]);

  const loadReport = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const nextReport = await fetchChannelSyncReliabilityReport(buildParams());
      setReport(nextReport);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load channel sync reliability report');
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    void loadReport();
  }, []);

  const handleApplyFilters = async (event: React.FormEvent) => {
    event.preventDefault();
    await loadReport();
  };

  const handleExportCsv = async () => {
    try {
      setIsExporting(true);
      await downloadChannelSyncReliabilityReportCsv(buildParams());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export channel sync reliability report');
    } finally {
      setIsExporting(false);
    }
  };

  const dailyTrend = report
    ? collapseEmptyHistory(report.daily_trend, {
        periodKey: (item) => item.date,
        hasActivity: channelSyncTrendHasActivity,
        showEmptyDays,
        generatedAt: report.generated_at,
      })
    : [];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Reports: Channel Sync Reliability</h1>
          <p style={styles.subtitle}>
            Daily reliability rollup of channel dispatch performance, including success and failure rates, sync duration distribution, and dominant failure classes.
          </p>
        </div>
        <div style={styles.headerLinks}>
          <Link to="/reports/channel-parity" style={styles.linkButton}>Open Channel Parity</Link>
          <Link to="/reports/reconciliation" style={styles.linkButton}>Inbound recon history</Link>
        </div>
      </div>

      <ReportGeneratedHeader
        generatedAt={report?.generated_at}
        onRefresh={() => {
          void loadReport();
        }}
        refreshing={isLoading}
      />

      <form style={styles.filtersRow} onSubmit={handleApplyFilters}>
        <label style={styles.filterLabel}>
          Window (days)
          <input
            type="number"
            min={14}
            max={90}
            value={days}
            onChange={(event) => setDays(event.target.value)}
            style={styles.input}
          />
        </label>

        <label style={styles.filterLabel}>
          Channel
          <input
            value={channel}
            onChange={(event) => setChannel(event.target.value)}
            style={styles.input}
            placeholder="shopify"
          />
        </label>

        <div style={styles.actions}>
          <button type="submit" style={styles.primaryButton} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Apply'}
          </button>
          <button type="button" style={styles.secondaryButton} onClick={handleExportCsv} disabled={isLoading || isExporting}>
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </form>

      {error && <div style={styles.error}>{error}</div>}

      {report && (
        <>
          <div style={styles.summaryGrid}>
            <MetricCard label="Successful Dispatches" value={String(report.summary.total_success_count)} />
            <MetricCard label="Failed Dispatches" value={String(report.summary.total_failed_count)} />
            <MetricCard
              label="Overall Failure Rate"
              value={`${computeFailureRate(report.summary.total_success_count, report.summary.total_failed_count).toFixed(2)}%`}
            />
            <MetricCard label="Window" value={`${report.params.days} days`} />
          </div>

          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Channel Comparison (Shopify vs Manapool)</h2>
            </div>
            <div style={styles.comparisonGrid}>
              <ComparisonCard title="Shopify" value={report.comparison.shopify} />
              <ComparisonCard title="Manapool" value={report.comparison.manapool} />
            </div>
          </section>

          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Channel Rollup</h2>
              <span style={styles.metaText}>{report.summary.channels.length} channels</span>
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Channel</th>
                    <th style={styles.th}>Success</th>
                    <th style={styles.th}>Failed</th>
                    <th style={styles.th}>Failure %</th>
                    <th style={styles.th}>p50 Duration</th>
                    <th style={styles.th}>p95 Duration</th>
                    <th style={styles.th}>Last Successful Sync</th>
                  </tr>
                </thead>
                <tbody>
                  {report.summary.channels.length === 0 && (
                    <tr>
                      <td colSpan={7} style={styles.emptyCell}>No channel dispatch attempts exist for the selected filters.</td>
                    </tr>
                  )}
                  {report.summary.channels.map((item) => (
                    <tr key={item.channel}>
                      <td style={styles.tdStrong}>{item.channel}</td>
                      <td style={styles.td}>{item.success_count}</td>
                      <td style={styles.td}>{item.failed_count}</td>
                      <td style={styles.td}>{item.failure_rate_pct.toFixed(2)}%</td>
                      <td style={styles.td}>{formatDuration(item.p50_duration_seconds)}</td>
                      <td style={styles.td}>{formatDuration(item.p95_duration_seconds)}</td>
                      <td style={styles.td}>{formatTimestamp(item.last_successful_sync_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div style={styles.sectionGrid}>
            <section style={styles.card}>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle}>Top Error Classes</h2>
              </div>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Error Class</th>
                      <th style={styles.th}>Count</th>
                      <th style={styles.th}>First Seen</th>
                      <th style={styles.th}>Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.top_error_classes.length === 0 && (
                      <tr>
                        <td colSpan={4} style={styles.emptyCell}>No failed dispatch attempts were recorded.</td>
                      </tr>
                    )}
                    {report.top_error_classes.map((item) => (
                      <tr key={item.error_class}>
                        <td style={styles.td}>{item.error_class}</td>
                        <td style={styles.td}>{item.count}</td>
                        <td style={styles.td}>{formatTimestamp(item.first_seen_at)}</td>
                        <td style={styles.td}>{formatTimestamp(item.last_seen_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section style={styles.card}>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle}>Daily Trend (14+ days)</h2>
                <ShowEmptyDaysToggle checked={showEmptyDays} onChange={setShowEmptyDays} />
              </div>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Channel</th>
                      <th style={styles.th}>Success</th>
                      <th style={styles.th}>Failed</th>
                      <th style={styles.th}>Failure %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyTrend.length === 0 && (
                      <tr>
                        <td colSpan={5} style={styles.emptyCell}>
                          {report.daily_trend.length === 0
                            ? 'No daily trend rows were generated.'
                            : 'No activity in this window.'}
                        </td>
                      </tr>
                    )}
                    {dailyTrend.map((item) => (
                      <tr key={`${item.date}-${item.channel}`}>
                        <td style={styles.td}>{item.date}</td>
                        <td style={styles.td}>{item.channel}</td>
                        <td style={styles.td}>{item.success_count}</td>
                        <td style={styles.td}>{item.failed_count}</td>
                        <td style={styles.td}>{item.failure_rate_pct.toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
};

function ComparisonCard({
  title,
  value,
}: {
  title: string;
  value: ChannelSyncReliabilityReportResponse['comparison']['shopify'];
}) {
  return (
    <div style={styles.metricCard}>
      <span style={styles.metricLabel}>{title}</span>
      {value ? (
        <>
          <strong style={styles.metricValue}>{value.failure_rate_pct.toFixed(2)}%</strong>
          <span style={styles.metricDetail}>
            {value.success_count} success / {value.failed_count} failed
          </span>
          <span style={styles.metricDetail}>p95: {formatDuration(value.p95_duration_seconds)}</span>
          <span style={styles.metricDetail}>Last success: {formatTimestamp(value.last_successful_sync_at)}</span>
        </>
      ) : (
        <span style={styles.metricDetail}>No data</span>
      )}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.metricCard}>
      <span style={styles.metricLabel}>{label}</span>
      <strong style={styles.metricValue}>{value}</strong>
    </div>
  );
}

function computeFailureRate(success: number, failed: number): number {
  const total = success + failed;
  if (total === 0) {
    return 0;
  }
  return (failed / total) * 100;
}

function formatDuration(value: number | null): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (value < 60) {
    return `${value.toFixed(0)}s`;
  }
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  return `${minutes}m ${seconds}s`;
}

function formatTimestamp(value: string | null): string {
  return formatInstant(value, '-');
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.lg,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.md,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    marginTop: spacing.xs,
    marginBottom: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
    maxWidth: '960px',
  },
  headerLinks: {
    display: 'flex',
    gap: spacing.sm,
  },
  linkButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    textDecoration: 'none',
    color: colors.text,
    backgroundColor: colors.surface,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'end',
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  filterLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    minWidth: '150px',
  },
  input: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.body.fontSize,
    color: colors.text,
    backgroundColor: colors.background,
  },
  actions: {
    display: 'flex',
    gap: spacing.sm,
  },
  primaryButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  secondaryButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  error: {
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: spacing.md,
  },
  metricCard: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  metricLabel: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  metricValue: {
    color: colors.text,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
  },
  metricDetail: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  sectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: spacing.md,
  },
  card: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.sm,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: spacing.sm,
  },
  sectionTitle: {
    margin: 0,
    color: colors.text,
    fontSize: typography.h3.fontSize,
    fontWeight: typography.h3.fontWeight,
  },
  metaText: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  comparisonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: spacing.md,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typography.body.fontSize,
    verticalAlign: 'top',
  },
  tdStrong: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    verticalAlign: 'top',
  },
  emptyCell: {
    padding: spacing.lg,
    textAlign: 'center',
    color: colors.textSecondary,
  },
};
